import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { testService } from '../../../services/testService';
import { useAssessmentContext } from '../AssessmentContext';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import toast from 'react-hot-toast';
import QuestionBankManager from '../QuestionBank/QuestionBankManager';
import TemplateBuilder from '../Templates/TemplateBuilder';
import GroupsInlineSearchField from '../dashboard/GroupsInlineSearchField';
import GroupsPickerDropdownPortal from '../dashboard/GroupsPickerDropdownPortal';
import { GroupsPickerMemberRow } from '../dashboard/GroupsPickerDropdownPanel';
import RoleSelectionSummary from '../dashboard/RoleSelectionSummary';
import { 
  FiCheck, 
  FiFileText, 
  FiLayers, 
  FiUsers, 
  FiTrash2, 
  FiPlus, 
  FiArrowRight, 
  FiArrowLeft, 
  FiSave,
  FiVideo,
  FiAlertTriangle,
  FiPlayCircle,
  FiClock,
  FiCheckCircle,
  FiLogOut,
  FiUploadCloud,
  FiSkipBack,
  FiMaximize,
  FiChevronDown,
  FiCalendar,
  FiSearch,
  FiEdit
} from 'react-icons/fi';

const STEPS = [
  { id: 'create', label: 'Create Test', path: '/workflow/create' },
  { id: 'question-bank', label: 'Question Bank', path: '/workflow/question-bank' },
  { id: 'template', label: 'Template', path: '/workflow/template' },
  { id: 'groups', label: 'Groups & Assign', path: '/workflow/groups' },
  { id: 'publish', label: 'Save', path: '/workflow/publish' }
];

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  try {
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
      return timeStr;
    }
    const parts = timeStr.split(':');
    let hours = parseInt(parts[0], 10);
    let minutes = parseInt(parts[1] || '0', 10);
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours % 12;
    displayHour = displayHour ? displayHour : 12;
    const hPad = String(displayHour).padStart(2, '0');
    const mPad = String(minutes).padStart(2, '0');
    return `${hPad}:${mPad} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const getEarliestIncompleteStep = (draft) => {
  if (!draft || !draft.name || !draft.category || !draft.type || !draft.instructions || (draft.videoEnabled && (!draft.videoFiles || draft.videoFiles.length === 0))) {
    return 'create';
  }
  if (!draft.questionBank || !draft.questionBank.name || !draft.questionBank.questions || draft.questionBank.questions.length === 0) {
    return 'question-bank';
  }
  if (!draft.template || !draft.template.name || !draft.template.sections || draft.template.sections.length === 0) {
    return 'template';
  }
  if (!draft.groups || draft.groups.length === 0) {
    return 'groups';
  }
  return 'publish';
};

const generateRandomCode = () => `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const TimePicker12Hour = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [position, setPosition] = useState(null);

  let initialHour = 10;
  let initialMinute = 0;
  let initialAmpm = 'AM';

  if (value) {
    const parts = value.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0], 10);
      initialMinute = parseInt(parts[1], 10);
      if (isNaN(h)) h = 10;
      if (isNaN(initialMinute)) initialMinute = 0;

      initialAmpm = h >= 12 ? 'PM' : 'AM';
      initialHour = h % 12;
      if (initialHour === 0) initialHour = 12;
    }
  }

  const syncPosition = useCallback(() => {
    if (!dropdownRef.current) return null;
    const rect = dropdownRef.current.getBoundingClientRect();
    const width = 160; // w-[160px]
    let left = rect.left;
    const viewportTop = rect.bottom + 4;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) {
      left = 8;
    }
    return { top: viewportTop, left };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const next = syncPosition();
    if (next) setPosition(next);
  }, [isOpen, syncPosition]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourRef.current) {
          const selected = hourRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
        if (minuteRef.current) {
          const selected = minuteRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      }, 30);
    }
  }, [isOpen]);

  const handleValChange = (newH, newM, newAmpm) => {
    let h24 = newH;
    if (newAmpm === 'PM' && h24 < 12) h24 += 12;
    if (newAmpm === 'AM' && h24 === 12) h24 = 0;

    const hStr = String(h24).padStart(2, '0');
    const mStr = String(newM).padStart(2, '0');
    onChange(`${hStr}:${mStr}`);
  };

  const formattedHour = String(initialHour).padStart(2, '0');
  const formattedMinute = String(initialMinute).padStart(2, '0');

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between bg-[#fcfdfe] border rounded-[0.875rem] px-3 py-1.5 cursor-pointer shadow-sm select-none transition-all w-full hover:bg-slate-50 border-slate-100 focus:outline-none ${
          isOpen ? 'border-red-500 ring-2 ring-red-100' : ''
        } ${disabled ? 'opacity-80 cursor-default pointer-events-none' : ''}`}
      >
        <span className="text-xs font-bold text-slate-700 tracking-wide">
          {formattedHour}:{formattedMinute} {initialAmpm}
        </span>
        <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && position && createPortal(
        <div 
          ref={dropdownPanelRef}
          className="bg-white border border-slate-200 rounded-2xl shadow-xl p-2.5 flex flex-col gap-2 w-[160px]"
          style={{ 
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 999999
          }}
        >
          <style>{`
            .custom-timepicker-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Selected Display Row */}
          <div className="grid grid-cols-3 gap-1 pb-2 border-b border-slate-100">
            <div className="h-8 rounded border-2 border-slate-800 bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedHour}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedMinute}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {initialAmpm}
            </div>
          </div>

          {/* Picker Columns */}
          <div className="flex justify-between gap-1 h-[160px]">
            {/* Hour Column */}
            <div 
              ref={hourRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                const hVal = String(h).padStart(2, '0');
                const isSelected = h === initialHour;
                return (
                  <button
                    type="button"
                    key={h}
                    onClick={() => handleValChange(h, initialMinute, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {hVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* Minute Column */}
            <div 
              ref={minuteRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => {
                const mVal = String(m).padStart(2, '0');
                const isSelected = m === initialMinute;
                return (
                  <button
                    type="button"
                    key={m}
                    onClick={() => handleValChange(initialHour, m, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {mVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* AM/PM Column */}
            <div 
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {['AM', 'PM'].map((ampm) => {
                const isSelected = ampm === initialAmpm;
                return (
                  <button
                    type="button"
                    key={ampm}
                    onClick={() => handleValChange(initialHour, initialMinute, ampm)}
                    className={`w-full py-1.5 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {ampm}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const WorkflowWizard = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const { navigateToModule } = useAssessmentContext();
  const fileInputRef = useRef(null);

  const handleHeaderPlusClick = (module) => {
    if (navigateToModule) {
      navigateToModule(module, 'inventory');
    }
    localStorage.setItem('hr_upload_return_path', '/workflow/groups');
    localStorage.setItem('hr_upload_force_new', 'true');
    navigate('/assessment');
  };

  // Wizard state
  const [draft, setDraft] = useState({
    id: generateRandomCode(),
    name: '',
    category: 'Internal',
    type: '',
    instructions: '',
    videoEnabled: false,
    videoFiles: [],
    navigationControl: false,
    autoSubmit: false,
    fullscreenMode: false,
    tabSwitchWarning: false,
    status: 'Draft',
    currentStep: 'create',
    completedSteps: [],
    questionBank: null,
    template: null,
    groups: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  // UI / popups state
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [showSuccess, setShowSuccess] = useState(false);

  // Categories list state
  const [categoriesList, setCategoriesList] = useState([]);

  useEffect(() => {
    testService.getCategories()
      .then(res => {
        if (res.status === 'success' && Array.isArray(res.categories)) {
          setCategoriesList(res.categories);
        }
      })
      .catch(err => {
        console.error('Failed to load categories', err);
      });
  }, []);

  const isHrCategory = (categoryName) => {
    const matched = categoriesList.find(c => String(c.name).trim().toLowerCase() === String(categoryName || '').trim().toLowerCase());
    if (matched) {
      return Number(matched.is_hr_type) === 1;
    }
    const cat = String(categoryName || '').trim();
    return cat === 'HR Recruitment-Fresher' || cat === 'External';
  };

  // Step 2: Question Banks state
  const [questionBanks, setQuestionBanks] = useState([]);
  const [loadingQB, setLoadingQB] = useState(false);
  const [selectedQB, setSelectedQB] = useState(null);
  const [qbSearchQuery, setQbSearchQuery] = useState('');

  // Step 3: Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Step 4: Groups state
  const [showInputRow, setShowInputRow] = useState(true);
  const [showRolesDropdown, setShowRolesDropdown] = useState(false);
  const [showCandidatesDropdown, setShowCandidatesDropdown] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [jobRoles, setJobRoles] = useState([]);
  const [collegeTemplates, setCollegeTemplates] = useState([]);
  const [candidatesList, setCandidatesList] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [newGroup, setNewGroup] = useState({
    pack_name: '',
    template_name: '',
    user_role: [],
    candidates: [],
    scheduled_date: '',
    start_time: '',
    end_time: '',
    duration: 0
  });
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);

  const groupRolesDropdownRef = useRef(null);
  const groupRolesPanelRef = useRef(null);
  const dropdownRef = useRef(null);
  const employeeDropdownPanelRef = useRef(null);
  const dateInputRef = useRef(null);

  const getRolesForForm = () => {
    if (isHrCategory(draft.category)) {
      return collegeTemplates.map(t => ({
        id: t.id,
        name: t.name
      }));
    }
    return jobRoles;
  };

  const resolveRoleNames = (roleIds) => {
    if (!roleIds) return [];
    const ids = Array.isArray(roleIds)
      ? roleIds.map(String)
      : String(roleIds).split(',').map((s) => s.trim()).filter(Boolean);
    const source = getRolesForForm();
    return ids.map((id) => {
      const found = source.find((r) => String(r.id) === id);
      return found ? found.name : id;
    });
  };

  const visibleGroupRoles = getRolesForForm().filter((role) =>
    (role.name || '').toLowerCase().includes(roleSearchTerm.toLowerCase())
  );

  const toggleGroupRole = (roleId) => {
    const current = Array.isArray(newGroup.user_role)
      ? newGroup.user_role.map(String)
      : String(newGroup.user_role || '').split(',').map(s => s.trim()).filter(Boolean);
    const updated = current.includes(String(roleId))
      ? current.filter((id) => id !== String(roleId))
      : [...current, String(roleId)];
    setNewGroup({ ...newGroup, user_role: updated, candidates: [] });
  };

  const selectAllGroupRoles = (checked) => {
    if (checked) {
      const allIds = visibleGroupRoles.map((r) => String(r.id));
      setNewGroup({ ...newGroup, user_role: allIds, candidates: [] });
    } else {
      setNewGroup({ ...newGroup, user_role: [], candidates: [] });
    }
  };

  const getVisibleEmployees = () =>
    candidatesList.filter(
      (emp) =>
        (emp.first_name || emp.name || '').toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
        (emp.email && emp.email.toLowerCase().includes(candidateSearchTerm.toLowerCase()))
    );

  const toggleEmployee = (empId) => {
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    const updated = current.includes(String(empId))
      ? current.filter(id => id !== String(empId))
      : [...current, String(empId)];
    setNewGroup({ ...newGroup, candidates: updated });
  };

  const isHrFresher = isHrCategory(draft.category);

  const isCollegeTemplateSelected = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    return ids.length > 0 && ids.every(id => current.includes(id));
  };

  const toggleCollegeTemplate = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(newGroup.candidates) ? newGroup.candidates.map(String) : [];
    const allSelected = ids.length > 0 && ids.every(id => current.includes(id));
    const updated = allSelected
      ? current.filter(id => !ids.includes(id))
      : [...new Set([...current, ...ids])];
    setNewGroup({ ...newGroup, candidates: updated });
  };

  const getVisibleCollegeTemplates = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(candidateSearchTerm.toLowerCase())
  );

  // Handle click outside for roles dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = groupRolesDropdownRef.current?.contains(event.target);
      const inPanel = groupRolesPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setShowRolesDropdown(false);
      }
    };
    if (showRolesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setRoleSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRolesDropdown]);

  // Handle click outside for candidates dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current?.contains(event.target);
      const inPanel = employeeDropdownPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setShowCandidatesDropdown(false);
      }
    };
    if (showCandidatesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setCandidateSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCandidatesDropdown]);

  // Handle click outside for date picker to force blur
  useEffect(() => {
    const handleClickOutsideDate = (event) => {
      if (dateInputRef.current && !dateInputRef.current.contains(event.target)) {
        setTimeout(() => {
          if (dateInputRef.current) {
            dateInputRef.current.blur();
          }
        }, 100);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideDate);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDate);
    };
  }, []);

  // Load draft from localStorage on mount/step change
  useEffect(() => {
    const draftStr = localStorage.getItem('draft_assessment');
    if (draftStr) {
      try {
        const parsed = JSON.parse(draftStr);
        if (parsed) {
          setDraft(prev => ({ ...prev, ...parsed }));
          if (parsed.questionBank) {
            setSelectedQB(parsed.questionBank);
          }
          if (parsed.template) {
            setSelectedTemplate(parsed.template);
          }
        }
      } catch (e) {
        console.error('Failed to parse draft_assessment', e);
      }
    }
  }, [step]);

  // Validation redirect interceptor
  useEffect(() => {
    const draftStr = localStorage.getItem('draft_assessment');
    let currentDraft = draft;
    if (draftStr) {
      try {
        currentDraft = JSON.parse(draftStr);
      } catch (e) {}
    }

    const earliestStep = getEarliestIncompleteStep(currentDraft);
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const currentStepIndex = stepOrder.indexOf(step);
    const earliestStepIndex = stepOrder.indexOf(earliestStep);

    if (currentStepIndex > earliestStepIndex) {
      toast.error(`Please complete the ${stepOrder[earliestStepIndex].replace('-', ' ')} step first.`);
      navigate(`/workflow/${earliestStep}`);
    }
  }, [step, navigate]);

  // Fetch Step 2: Question Banks
  useEffect(() => {
    if (step === 'question-bank') {
      setLoadingQB(true);
      testService.getQuestionBanks()
        .then(res => {
          if (res.status === 'success' && res.banks) {
            setQuestionBanks(res.banks);
            const draftStr = localStorage.getItem('draft_assessment');
            const parsed = draftStr ? JSON.parse(draftStr) : null;
            if (parsed?.questionBank?.name) {
              const matched = res.banks.find(b => b.name === parsed.questionBank.name);
              if (matched) {
                setSelectedQB(matched);
                if (!parsed.questionBank.id) {
                  updateDraft({
                    questionBank: {
                      ...parsed.questionBank,
                      id: matched.id
                    }
                  });
                }
              }
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to load question banks');
        })
        .finally(() => setLoadingQB(false));
    }
  }, [step]);

  // Fetch Step 3: Templates
  useEffect(() => {
    if (step === 'template' || step === 'groups') {
      setLoadingTemplates(true);
      testService.getTemplates()
        .then(res => {
          if (res.status === 'success' && res.templates) {
            setTemplates(res.templates);
            const draftStr = localStorage.getItem('draft_assessment');
            const parsed = draftStr ? JSON.parse(draftStr) : null;
            if (parsed?.template?.name) {
              const matched = res.templates.find(t => t.name === parsed.template.name);
              if (matched) {
                setSelectedTemplate(matched);
                if (!parsed.template.id) {
                  updateDraft({
                    template: {
                      ...parsed.template,
                      id: matched.id
                    }
                  });
                }
              }
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to load templates');
        })
        .finally(() => setLoadingTemplates(false));
    }
  }, [step]);

  // Fetch Step 4 Roles / Colleges
  useEffect(() => {
    if (step === 'groups') {
      testService.getJobRoles()
        .then(res => {
          if (res.status === 'success') {
            setJobRoles(res.data || []);
          } else if (Array.isArray(res)) {
            setJobRoles(res);
          }
        })
        .catch(e => console.error('Error fetching job roles', e));

      testService.getHrCollegeTemplates()
        .then(res => {
          if (res.status === 'success' && res.college_templates) {
            const mapped = (res.college_templates || []).map((item) => ({
              id: item.template_name || item.name || '',
              name: item.template_name || item.name || '',
              student_count: parseInt(item.student_count || item.total_students) || 0,
              student_ids: Array.isArray(item.student_ids)
                ? item.student_ids.map(String)
                : String(item.student_ids || '').split(',').map(s => s.trim()).filter(Boolean)
            }));
            setCollegeTemplates(mapped);
          }
        })
        .catch(e => console.error('Error fetching college templates', e));
    }
  }, [step]);

  // Reset inline group form state when category changes
  useEffect(() => {
    setNewGroup({
      pack_name: '',
      template_id: '',
      template_name: '',
      user_role: [],
      candidates: [],
      scheduled_date: new Date().toISOString().split('T')[0],
      start_time: '10:00',
      end_time: '11:00',
      duration: 60
    });
    setCandidatesList([]);
  }, [draft.category]);

  // Fetch candidates list when roles change in Add Group modal
  useEffect(() => {
    if (step === 'groups' && newGroup.user_role.length > 0) {
      setLoadingCandidates(true);
      if (!isHrCategory(draft.category)) {
        testService.getUsersByRoles(newGroup.user_role.join(','))
          .then(res => {
            const list = res.data || (Array.isArray(res) ? res : []);
            const mapped = list.map(emp => ({
              id: emp.id,
              first_name: emp.first_name || emp.name || '',
              last_name: emp.last_name || '',
              email: emp.email || emp.username || '',
            }));
            setCandidatesList(mapped);
          })
          .catch(e => console.error(e))
          .finally(() => setLoadingCandidates(false));
      } else {
        // External Category: Fetch candidates/students for all selected roles (colleges)
        Promise.all(
          newGroup.user_role.map(roleName => 
            testService.getStudentsByCollege(roleName)
              .then(res => res.students || [])
              .catch(() => [])
          )
        ).then(results => {
          const allStudents = results.flat();
          const uniqueStudents = [];
          const seenIds = new Set();
          allStudents.forEach(s => {
            const uniqueId = String(s.id || s.username || s.email || '');
            if (uniqueId && !seenIds.has(uniqueId)) {
              seenIds.add(uniqueId);
              uniqueStudents.push({
                id: s.id,
                first_name: s.student_name || s.name || '',
                last_name: '',
                email: s.email || s.username || '',
              });
            }
          });
          setCandidatesList(uniqueStudents);
        })
        .catch(e => console.error(e))
        .finally(() => setLoadingCandidates(false));
      }
    } else {
      setCandidatesList([]);
    }
  }, [newGroup.user_role, draft.category, step]);

  const updateDraft = (fields) => {
    const updated = {
      ...draft,
      ...fields,
      updatedAt: new Date().toISOString()
    };
    setDraft(updated);
    localStorage.setItem('draft_assessment', JSON.stringify(updated));
    window.dispatchEvent(new Event('draft-updated'));
  };

  // Stepper Header click handler
  const handleStepClick = (targetStep) => {
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const targetIndex = stepOrder.indexOf(targetStep.id);
    const earliestStep = getEarliestIncompleteStep(draft);
    const earliestIndex = stepOrder.indexOf(earliestStep);

    // Can only click back to completed steps or the earliest incomplete step
    if (targetIndex <= earliestIndex || draft.completedSteps.includes(targetStep.id)) {
      navigate(targetStep.path);
    }
  };

  const handleSaveAndExit = () => {
    updateDraft({ currentStep: step });
    toast.success('Draft test saved successfully!');
    localStorage.setItem('assessment_active_module', 'inventory');
    navigate('/assessment');
  };

  const handleWizardBack = () => {
    const stepOrder = ['create', 'question-bank', 'template', 'groups', 'publish'];
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      updateDraft({ currentStep: prevStep });
      navigate('/workflow/' + prevStep);
    } else {
      localStorage.setItem('assessment_active_module', 'inventory');
      navigate('/assessment');
    }
  };

  const handleSaveAndContinue = async () => {
    // Validate current step
    if (step === 'create') {
      if (!draft.name.trim()) return toast.error('Test name is required');
      if (!draft.type) return toast.error('Test type is required');
      if (!draft.instructions.trim()) return toast.error('Instructions are required');
      if (draft.videoEnabled && (!draft.videoFiles || draft.videoFiles.length === 0)) return toast.error('Please upload at least one video file');

      const completed = [...new Set([...draft.completedSteps, 'create'])];
      updateDraft({
        completedSteps: completed,
        currentStep: 'question-bank'
      });
      navigate('/workflow/question-bank');
    } 
    else if (step === 'question-bank') {
      if (!selectedQB) return toast.error('Please select a question bank');
      if (!selectedQB.name.trim()) return toast.error('Please provide a name for the Question Bank');
      if (!selectedQB.questions || selectedQB.questions.length === 0) {
        return toast.error('Please add at least one question to the bank');
      }

      const savingToast = toast.loading('Saving Question Bank...');
      try {
        let repoId = selectedQB.id;
        if (!repoId) {
          const bankRes = await testService.saveQuestionBank({ name: selectedQB.name });
          if (bankRes.status === 'success') {
            repoId = bankRes.id;
          } else {
            throw new Error(bankRes.message || "Failed to create Question Bank");
          }
        }

        const bulkRes = await testService.bulkSaveQBQuestions({
          repository_id: repoId,
          questions: selectedQB.questions
        });

        if (bulkRes.status !== 'success') {
          throw new Error(bulkRes.message || "Failed to save Questions");
        }

        toast.dismiss(savingToast);
        toast.success('Question Bank saved successfully!');

        const updatedQB = {
          ...selectedQB,
          id: repoId
        };
        setSelectedQB(updatedQB);
        
        const completed = [...new Set([...draft.completedSteps, 'question-bank'])];
        updateDraft({
          questionBank: {
            id: updatedQB.id,
            name: updatedQB.name,
            questions: updatedQB.questions || []
          },
          completedSteps: completed,
          currentStep: 'template'
        });
        navigate('/workflow/template');
      } catch (err) {
        toast.dismiss(savingToast);
        toast.error(err.message || 'Failed to save Question Bank');
        console.error(err);
      }
    }
    else if (step === 'template') {
      if (!selectedTemplate) return toast.error('Please select a template');
      if (!selectedTemplate.name.trim()) return toast.error('Please enter a Template Name');
      if (!selectedTemplate.sections || selectedTemplate.sections.length === 0) {
        return toast.error('Please add at least one section');
      }

      const savingToast = toast.loading('Saving Template...');
      try {
        const payload = {
          id: selectedTemplate.id,
          name: selectedTemplate.name,
          passMark: (selectedTemplate.passMark !== undefined && selectedTemplate.passMark !== null && selectedTemplate.passMark !== '') ? Number(selectedTemplate.passMark) : 0,
          totalQuestions: selectedTemplate.questions?.length || 0,
          totalMarks: selectedTemplate.questions?.reduce((sum, q) => sum + Number(q.marks || 0), 0) || 0,
          bank: selectedTemplate.bank || selectedTemplate.questionBank || '',
          sections: selectedTemplate.sections,
          questions: selectedTemplate.questions || []
        };
        const res = await testService.saveTemplate(payload);
        if (res.status === 'success') {
          toast.dismiss(savingToast);
          toast.success(selectedTemplate.id ? 'Template updated successfully!' : 'Template saved successfully!');
          
          const updatedTemplate = {
            ...selectedTemplate,
            id: res.id
          };
          setSelectedTemplate(updatedTemplate);

          const completed = [...new Set([...draft.completedSteps, 'template'])];
          updateDraft({
            template: {
              id: updatedTemplate.id,
              name: updatedTemplate.name,
              questionBank: updatedTemplate.bank || updatedTemplate.questionBank || '',
              passMark: (updatedTemplate.passMark !== undefined && updatedTemplate.passMark !== null && updatedTemplate.passMark !== '') ? Number(updatedTemplate.passMark) : 0,
              sections: updatedTemplate.sections || []
            },
            completedSteps: completed,
            currentStep: 'groups'
          });
          navigate('/workflow/groups');
        } else {
          throw new Error(res.message || "Failed to save template");
        }
      } catch (err) {
        toast.dismiss(savingToast);
        toast.error(err.message || 'Failed to save Template');
        console.error(err);
      }
    }
    else if (step === 'groups') {
      if (editingGroupId) {
        handleAddGroupSubmit();
        return;
      }
      if (!draft.groups || draft.groups.length === 0) return toast.error('Please assign at least one group');

      const completed = [...new Set([...draft.completedSteps, 'groups'])];
      updateDraft({
        completedSteps: completed,
        currentStep: 'publish'
      });
      navigate('/workflow/publish');
    }
  };

  // Question bank selection handler
  const handleSelectQB = (qb) => {
    setSelectedQB(qb);
  };

  // Template selection handler
  const handleSelectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
  };

  // Group creation handlers
  const handleCalculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let diff = (eH * 60 + eM) - (sH * 60 + sM);
    if (diff < 0) diff += 1440;
    return diff;
  };

  const handleTimeFieldChange = (field, val) => {
    const updated = { ...newGroup, [field]: val };
    const dur = handleCalculateDuration(updated.start_time, updated.end_time);
    setNewGroup({ ...updated, duration: dur });
  };

  const handleAddGroupSubmit = (e) => {
    if (e) e.preventDefault();
    if (!newGroup.pack_name.trim()) return toast.error('Group name is required');
    if (!newGroup.scheduled_date) return toast.error('Date is required');
    if (!newGroup.start_time || !newGroup.end_time) return toast.error('Start and End times are required');

    if (isHrFresher && newGroup.user_role.length === 0) {
      return toast.error('Please select a college name');
    }
    if (!isHrFresher && newGroup.user_role.length === 0) {
      return toast.error('Please select at least one role');
    }
    if (isHrFresher && newGroup.candidates.length === 0) {
      return toast.error('The selected college contains no candidates');
    }
    if (!isHrFresher && newGroup.candidates.length === 0) {
      return toast.error('Please select candidates');
    }

    const resolvedUserRole = newGroup.user_role.join(',');

    if (editingGroupId) {
      // Update existing group
      const updatedGroups = (draft.groups || []).map(g => {
        if (g.id === editingGroupId) {
          return {
            ...g,
            pack_name: newGroup.pack_name.trim(),
            template_id: draft.template?.id || '',
            template_name: draft.template?.name || 'Default Template',
            user_role: resolvedUserRole,
            candidates: newGroup.candidates.join(','),
            scheduled_date: newGroup.scheduled_date,
            start_time: newGroup.start_time,
            end_time: newGroup.end_time,
            duration: newGroup.duration
          };
        }
        return g;
      });
      updateDraft({ groups: updatedGroups });
      setEditingGroupId(null);
      toast.success('Group updated successfully');
    } else {
      // Create new group
      const groupObject = {
        id: 'group-' + Date.now(),
        pack_name: newGroup.pack_name.trim(),
        template_id: draft.template?.id || '',
        template_name: draft.template?.name || 'Default Template',
        user_role: resolvedUserRole,
        candidates: newGroup.candidates.join(','),
        scheduled_date: newGroup.scheduled_date,
        start_time: newGroup.start_time,
        end_time: newGroup.end_time,
        duration: newGroup.duration,
        status: 'Draft'
      };

      const updatedGroups = [...(draft.groups || []), groupObject];
      updateDraft({ groups: updatedGroups });
      toast.success('Group added successfully');
    }
    
    // Reset Add Group form state
    setNewGroup({
      pack_name: '',
      template_id: '',
      template_name: '',
      user_role: [],
      candidates: [],
      scheduled_date: new Date().toISOString().split('T')[0],
      start_time: '10:00',
      end_time: '11:00',
      duration: 60
    });
    setShowInputRow(false);
  };

  const handleDeleteGroup = (gId) => {
    const updated = draft.groups.filter(g => g.id !== gId);
    updateDraft({ groups: updated });
    if (editingGroupId === gId) {
      setEditingGroupId(null);
      setNewGroup({
        pack_name: '',
        template_id: '',
        template_name: '',
        user_role: [],
        candidates: [],
        scheduled_date: new Date().toISOString().split('T')[0],
        start_time: '10:00',
        end_time: '11:00',
        duration: 60
      });
    }
    toast.success('Group removed.');
  };

  const handleEditGroup = (g) => {
    setEditingGroupId(g.id);
    setNewGroup({
      pack_name: g.pack_name || '',
      template_id: g.template_id || '',
      template_name: g.template_name || '',
      user_role: Array.isArray(g.user_role)
        ? g.user_role.map(String)
        : String(g.user_role || '').split(',').map(s => s.trim()).filter(Boolean),
      candidates: Array.isArray(g.candidates)
        ? g.candidates.map(String)
        : String(g.candidates || '').split(',').map(s => s.trim()).filter(Boolean),
      scheduled_date: g.scheduled_date || '',
      start_time: g.start_time || '',
      end_time: g.end_time || '',
      duration: g.duration || 0
    });
    setShowInputRow(true);
  };

  const handleQuestionDoubleClick = (q) => {
    setViewingQuestion(q);
  };

  // Save handler
  const handlePublishAssessment = () => {
    // 1. Groups stay as 'Draft' — each group is published individually from the Groups tab
    const draftGroups = draft.groups.map(g => ({
      ...g,
      status: g.status || 'Draft'
    }));

    // 2. Normalize wizard field names to DB field names so that
    //    TestsDataTable (video indicator), TestExecutionPage (exam configs)
    //    and MemberAssessmentDashboard all read the same shape.
    const publishedAssessment = {
      ...draft,
      groups: draftGroups,
      status: 'Published',
      updatedAt: new Date().toISOString(),
      // Video — DB shape
      add_video: draft.videoEnabled ? 'Yes' : 'No',
      intro_videos: Array.isArray(draft.videoFiles) ? draft.videoFiles : [],
      // Exam config — DB shape
      allow_backtracking: draft.navigationControl ? 1 : 0,
      proctored_exam:     draft.fullscreenMode    ? 1 : 0,
      browser_lockdown:   draft.tabSwitchWarning  ? 1 : 0,
      show_results:       draft.autoSubmit        ? 1 : 0,
    };
    
    delete publishedAssessment.isEditMode;

    // 3. Save to published_tests in localStorage
    const existingPubStr = localStorage.getItem('published_tests');
    let publishedList = [];
    if (existingPubStr) {
      try {
        publishedList = JSON.parse(existingPubStr);
      } catch (e) {}
    }

    const existingIndex = publishedList.findIndex(t => String(t.id) === String(publishedAssessment.id));
    if (existingIndex > -1) {
      publishedList[existingIndex] = publishedAssessment;
    } else {
      publishedList.push(publishedAssessment);
    }
    localStorage.setItem('published_tests', JSON.stringify(publishedList));

    // 4. Clear the draft
    localStorage.removeItem('draft_assessment');

    // Clear from saved_drafts list as well to be safe
    if (draft && draft.id) {
      const savedDraftsStr = localStorage.getItem('saved_drafts');
      if (savedDraftsStr) {
        try {
          const savedDrafts = JSON.parse(savedDraftsStr);
          const filtered = savedDrafts.filter(d => String(d.id) !== String(draft.id) && d.name !== draft.name);
          localStorage.setItem('saved_drafts', JSON.stringify(filtered));
        } catch (e) {}
      }
    }

    window.dispatchEvent(new Event('draft-updated'));

    // 5. Show success screen
    setShowSuccess(true);
    localStorage.setItem('show_publish_reminder_popup', 'true');

    // 6. Redirect to dashboard /assessment after 1.8s
    setTimeout(() => {
      localStorage.setItem('assessment_active_module', 'inventory');
      navigate('/assessment');
    }, 1800);
  };

  // Reset draft handler
  const handleResetDraft = () => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Reset draft test?',
      message: 'Are you sure you want to completely discard this draft? This will clear all details and return you to the dashboard.',
      confirmText: 'Yes, discard it!',
      onConfirm: () => {
        localStorage.removeItem('draft_assessment');
        if (draft && draft.id) {
          const savedDraftsStr = localStorage.getItem('saved_drafts');
          if (savedDraftsStr) {
            try {
              const savedDrafts = JSON.parse(savedDraftsStr);
              const filtered = savedDrafts.filter(d => d.id !== draft.id);
              localStorage.setItem('saved_drafts', JSON.stringify(filtered));
            } catch (e) {}
          }
        }
        window.dispatchEvent(new Event('draft-updated'));
        setPopupConfig({ isOpen: false });
        toast.success('Draft test discarded.');
        localStorage.setItem('assessment_active_module', 'inventory');
        navigate('/assessment');
      }
    });
  };

  if (showSuccess) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
        <div className="w-24 h-24 bg-emerald-50 border-4 border-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 shadow-2xl mb-8 animate-bounce">
          <FiCheckCircle className="text-5xl" />
        </div>
        <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight mb-3">Test Saved Successfully!</h2>
        <p className="text-slate-500 font-bold max-w-md uppercase tracking-wider text-xs leading-relaxed">
          Redirecting back to dashboard in a moment...
        </p>
      </div>
    );
  }

  return (
    <div className="assess-mod-page p-4 w-full max-w-none flex flex-col h-full min-h-0 overflow-hidden relative">
      {/* Wizard Header / Brand bar */}
      {step !== 'publish' && (
        <div className="flex items-center justify-between mb-3 shrink-0">
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Test Setup Flow</h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Create QB button for Step 2 */}
            {step === 'question-bank' && (draft.questionBank?.id || selectedQB?.id) && (
              <button 
                onClick={() => {
                  setSelectedQB(null);
                  updateDraft({ questionBank: null });
                }}
                className="btn-assessment btn-assessment-danger flex items-center gap-2 text-xs font-bold"
              >
                <FiPlus className="text-sm" />
                <span>Create QB</span>
              </button>
            )}

            {/* Create Template button for Step 3 */}
            {step === 'template' && (draft.template?.id || selectedTemplate?.id) && (
              <button 
                onClick={() => {
                  setSelectedTemplate(null);
                  updateDraft({ template: null });
                }}
                className="btn-assessment btn-assessment-danger flex items-center gap-2 text-xs font-bold"
              >
                <FiPlus className="text-sm" />
                <span>Create New Template</span>
              </button>
            )}

            <button 
              onClick={handleWizardBack}
              className="btn-assessment btn-assessment-secondary flex items-center gap-2 text-xs font-bold border-red-100 text-red-500 hover:bg-red-50"
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
          </div>
        </div>
      )}

      {/* 5-Step Stepper Header */}
      {step !== 'publish' && (
        <div className="bg-white border border-slate-100 rounded-2xl py-3 px-6 shadow-xl shadow-slate-100/30 mb-3 overflow-x-auto shrink-0">
          <div className="flex items-center justify-between min-w-[700px] px-4">
            {STEPS.map((s, idx) => {
              const isCurrent = step === s.id;
              const isCompleted = draft.completedSteps.includes(s.id);
              
              return (
                <React.Fragment key={s.id}>
                  {/* Step Item */}
                  <div 
                    onClick={() => handleStepClick(s)}
                    className={`flex items-center gap-3 select-none cursor-pointer transition-all duration-300 ${
                      isCurrent ? 'scale-105' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs border transition-all duration-300 ${
                      isCurrent 
                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-200' 
                        : isCompleted 
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                          : 'bg-slate-50 border-slate-100 text-slate-400'
                    }`}>
                      {isCompleted ? <FiCheck className="text-sm stroke-[3]" /> : idx + 1}
                    </div>
                    <div>
                      <span className={`block text-xs font-bold uppercase tracking-wider ${
                        isCurrent 
                          ? 'text-red-600' 
                          : isCompleted 
                            ? 'text-slate-800' 
                            : 'text-slate-400'
                      }`}>
                        {s.label}
                      </span>
                      <span className="block text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        {isCurrent ? 'Current Step' : isCompleted ? 'Completed' : 'Upcoming'}
                      </span>
                    </div>
                  </div>

                  {/* Line Separator */}
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 transition-all duration-500 ${
                      isCompleted ? 'bg-emerald-200' : 'bg-slate-100'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Wizard Step Content Area */}
      <div className="flex-1 min-h-0 overflow-hidden mb-0 flex flex-col">
        
        {/* STEP 1: CREATE TEST */}
        {step === 'create' && (
          <div className="bg-white border border-slate-100 rounded-3xl p-8 pb-24 shadow-xl shadow-slate-100/30 animate-in fade-in duration-300 max-h-full overflow-y-auto custom-scrollbar">
            <h3 className="text-lg font-extrabold text-slate-800 tracking-tight mb-6">
              Create New Test
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start mb-6">
              <div className="lg:col-span-3">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Test Name *</label>
                <input
                  type="text"
                  value={draft.name || ''}
                  onChange={(e) => updateDraft({ name: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-4 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                  placeholder="e.g. Technical Proficiency Test"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Test Category *</label>
                <select
                  value={draft.category || 'Internal'}
                  onChange={(e) => updateDraft({ category: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-3 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                >
                  {categoriesList.length > 0 ? (
                    categoriesList.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="Internal">Internal</option>
                      <option value="External">External</option>
                    </>
                  )}
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Test Type *</label>
                <select
                  value={draft.type || ''}
                  onChange={(e) => updateDraft({ type: e.target.value })}
                  className="w-full h-11 bg-slate-50 border border-slate-100 rounded-xl px-3 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none transition-all shadow-inner"
                >
                  <option value="">Select type...</option>
                  <option value="Technical">Technical</option>
                  <option value="Compliance">Compliance</option>
                  <option value="Behavioral">Behavioral</option>
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Does it Include the Video? *</label>
                <select
                  value={draft.videoEnabled ? 'Yes' : 'No'}
                  onChange={(e) => {
                    const enabled = e.target.value === 'Yes';
                    updateDraft({
                      videoEnabled: enabled,
                      videoFiles: enabled ? draft.videoFiles : []
                    });
                  }}
                  className={`w-full h-11 bg-slate-50 border rounded-xl px-3 text-sm font-semibold focus:bg-white outline-none transition-all shadow-inner ${
                    draft.videoEnabled ? 'border-red-500 focus:ring-1 focus:ring-red-500' : 'border-slate-100 focus:border-red-500'
                  }`}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              {draft.videoEnabled && (
                <div className="lg:col-span-3 flex flex-col">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Upload Video *</label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-11 bg-slate-50 border border-dashed border-red-200 rounded-xl px-4 flex items-center justify-between cursor-pointer hover:bg-red-50/10 transition-all shadow-inner"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FiUploadCloud className="text-red-500 text-sm shrink-0" />
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide truncate">
                        {draft.videoFiles && draft.videoFiles.length > 0 
                          ? `${draft.videoFiles.length} VIDEO(S) SELECTED` 
                          : 'UPLOAD VIDEO (4MB AND BELOW)'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-[10px] font-black text-red-500 uppercase tracking-widest shrink-0"
                    >
                      BROWSE
                    </button>
                    <input 
                      type="file"
                      ref={fileInputRef}
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 4 * 1024 * 1024) {
                            toast.error('Video must be 4MB or below');
                            e.target.value = '';
                            return;
                          }
                          const currentFiles = draft.videoFiles || [];
                          const alreadyHasName = currentFiles.some(f => {
                            const n = typeof f === 'object' && f ? f.name : f;
                            return n === file.name;
                          });
                          if (alreadyHasName) {
                            toast.error('This video is already selected');
                            e.target.value = '';
                            return;
                          }
                          // Store File object in window cache (non-persisted memory) to prevent localStorage quota limit errors
                          window.selectedVideoFiles = window.selectedVideoFiles || {};
                          window.selectedVideoFiles[file.name] = file;

                          const objectUrl = URL.createObjectURL(file);
                          const newFileMeta = {
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            objectUrl: objectUrl
                          };
                          updateDraft({ videoFiles: [...currentFiles, newFileMeta] });
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>

                  {draft.videoFiles && draft.videoFiles.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {draft.videoFiles.map((fileObj, index) => {
                        const displayName = typeof fileObj === 'object' && fileObj ? fileObj.name : fileObj;
                        return (
                        <div key={index} className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                              <FiPlayCircle className="text-sm fill-red-500/10" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] font-bold text-slate-700 truncate max-w-[110px]" title={displayName}>
                                {displayName}
                              </div>
                              <div className="text-[8px] font-black text-slate-400 uppercase tracking-wider mt-0.5">
                                READY
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const name = typeof fileObj === 'object' && fileObj ? fileObj.name : null;
                              if (name && window.selectedVideoFiles) {
                                delete window.selectedVideoFiles[name];
                              }
                              if (fileObj && fileObj.objectUrl) {
                                try {
                                  URL.revokeObjectURL(fileObj.objectUrl);
                                } catch (err) {}
                              }
                              const updated = draft.videoFiles.filter((_, i) => i !== index);
                              updateDraft({ videoFiles: updated });
                            }}
                            className="text-slate-400 hover:text-red-500 transition-all p-1 shrink-0"
                          >
                            <FiTrash2 className="text-xs" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex flex-col h-full">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Instruction to the Exam Taker *</label>
                <textarea
                  value={draft.instructions || ''}
                  onChange={(e) => updateDraft({ instructions: e.target.value })}
                  className="w-full flex-1 bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-semibold focus:border-red-500 focus:bg-white outline-none resize-none transition-all shadow-inner min-h-[160px]"
                  placeholder="Briefly provide instructions..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Exam Configurations</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Navigation Control */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiSkipBack className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Allow users to move between questions</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ navigationControl: !draft.navigationControl })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.navigationControl ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.navigationControl ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Auto Submit */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiSave className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Submit automatically when the time ends</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ autoSubmit: !draft.autoSubmit })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.autoSubmit ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.autoSubmit ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Fullscreen Mode */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiMaximize className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Keep the exam in the full screen mode</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ fullscreenMode: !draft.fullscreenMode })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.fullscreenMode ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.fullscreenMode ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Tab Switch Warning */}
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-red-500 shrink-0">
                        <FiAlertTriangle className="text-md" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-black text-[#1e293b] leading-tight">Show warning when leaving exam tab</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraft({ tabSwitchWarning: !draft.tabSwitchWarning })}
                      className={`w-10 h-5 flex items-center rounded-full p-0.5 cursor-pointer transition-all duration-300 shrink-0 ${
                        draft.tabSwitchWarning ? 'bg-red-500' : 'bg-slate-200'
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                          draft.tabSwitchWarning ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: QUESTION BANK */}
        {step === 'question-bank' && (
          <div className="animate-in fade-in duration-300 flex-1 min-h-0 flex flex-col overflow-hidden">
            <QuestionBankManager 
              isWizardMode={true}
              selectedQBId={draft.questionBank?.id || selectedQB?.id}
              draftQuestionBank={draft.questionBank}
              onSelectQB={(qb) => {
                if (qb) {
                  setSelectedQB(qb);
                  if (qb.id !== draft.questionBank?.id || qb.name !== draft.questionBank?.name) {
                    updateDraft({
                      questionBank: {
                        id: qb.id,
                        name: qb.name,
                        questions: qb.questions || []
                      }
                    });
                  }
                } else {
                  setSelectedQB(null);
                  if (draft.questionBank) {
                    updateDraft({ questionBank: null });
                  }
                }
              }}
            />
          </div>
        )}

        {/* STEP 3: TEMPLATE */}
        {step === 'template' && (
          <div className="animate-in fade-in duration-300 flex-1 min-h-0 flex flex-col overflow-hidden">
            <TemplateBuilder 
              isWizardMode={true}
              selectedTemplateId={draft.template?.id || selectedTemplate?.id}
              draftTemplate={draft.template}
              onSelectTemplate={(tpl) => {
                if (tpl) {
                  setSelectedTemplate(tpl);
                  if (tpl.id !== draft.template?.id || tpl.name !== draft.template?.name) {
                    updateDraft({
                      template: {
                        id: tpl.id,
                        name: tpl.name,
                        questionBank: tpl.bank || '',
                        passMark: (tpl.passMark !== undefined && tpl.passMark !== null && tpl.passMark !== '') ? Number(tpl.passMark) : 0,
                        sections: tpl.sections || []
                      }
                    });
                  }
                } else {
                  setSelectedTemplate(null);
                  if (draft.template) {
                    updateDraft({ template: null });
                  }
                }
              }}
            />
          </div>
        )}

        {/* STEP 4: GROUPS & ASSIGN */}
        {step === 'groups' && (
          <div className="bg-white border border-slate-100 rounded-3xl p-8 pb-24 shadow-xl shadow-slate-100/30 animate-in fade-in duration-300 max-h-full overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between border-b pb-6 mb-6">
              <div className="flex items-center gap-4">
                <span className="text-sm font-black text-slate-800 tracking-wider">GROUPS</span>
              </div>
              <button 
                onClick={() => {
                  setNewGroup({
                    pack_name: '',
                    template_id: '',
                    template_name: '',
                    user_role: [],
                    candidates: [],
                    scheduled_date: new Date().toISOString().split('T')[0],
                    start_time: '10:00',
                    end_time: '11:00',
                    duration: 60,
                  });
                  setShowInputRow(true);
                }}
                className="px-4 py-2 bg-red-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-red-950 transition-all shadow-md"
              >
                <FiPlus />
                <span>Add Next Group</span>
              </button>
            </div>

            <div className="assess-mod-groups-panel-scroll">
              <div className="assess-mod-groups-panel">
                <table className="assess-mod-groups-accordion-table w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-name">Group Name</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-template">Select Template</th>
                      {!isHrFresher && (
                        <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-roles">Assigned Roles</th>
                      )}
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-assign">
                        <div className="flex items-center gap-1.5">
                          <span>Assign To</span>
                          {isHrFresher && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHeaderPlusClick('hrUpload');
                              }}
                              className="shrink-0 w-4 h-4 bg-red-800 text-white rounded-md flex items-center justify-center hover:bg-red-950 transition-colors shadow-sm"
                              title="External Setup Candidates Onboarding & Repository"
                              aria-label="External Setup Candidates Onboarding & Repository"
                            >
                              <FiPlus className="text-[8px] stroke-[2.5]" />
                            </button>
                          )}
                        </div>
                      </th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-date text-center">Date</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-start text-center">Start Time</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-end text-center">End Time</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-duration text-center">Duration</th>
                      <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest assess-mod-groups-col-action text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <style dangerouslySetInnerHTML={{__html: `
                      input[type="date"]::-webkit-calendar-picker-indicator,
                      input[type="time"]::-webkit-calendar-picker-indicator {
                        background: transparent;
                        bottom: 0;
                        color: transparent;
                        cursor: pointer;
                        height: auto;
                        left: 0;
                        position: absolute;
                        right: 0;
                        top: 0;
                        width: auto;
                      }
                    `}} />
                    {draft.groups?.map(g => {
                      const resolvedRoles = resolveRoleNames(g.user_role).join(', ');
                      const candCount = String(g.candidates || '').split(',').filter(Boolean).length;
                      return (
                        <tr key={g.id} className="border-b border-slate-50 hover:bg-slate-50/30 transition-colors">
                          <td className="py-4 font-bold text-slate-700 text-sm assess-mod-groups-col-name">{g.pack_name}</td>
                          <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-template">{g.template_name}</td>
                          {!isHrFresher && (
                            <td className="py-4 text-xs font-bold text-slate-500 max-w-[200px] truncate assess-mod-groups-col-roles" title={resolvedRoles}>
                              {resolvedRoles || 'N/A'}
                            </td>
                          )}
                          <td className="py-4 text-xs font-black text-slate-700 assess-mod-groups-col-assign">
                            {candCount} Candidates
                          </td>
                          <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-date text-center">{formatDateToDDMMYYYY(g.scheduled_date)}</td>
                          <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-start text-center">{g.start_time}</td>
                          <td className="py-4 text-xs font-semibold text-slate-500 assess-mod-groups-col-end text-center">{g.end_time}</td>
                          <td className="py-4 text-xs font-black text-slate-700 assess-mod-groups-col-duration text-center">{g.duration} Mins</td>
                          <td className="py-4 assess-mod-groups-col-action text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button 
                                type="button"
                                onClick={() => handleEditGroup(g)}
                                className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-500 text-blue-500 hover:text-white flex items-center justify-center transition-all border border-blue-100 shadow-sm"
                                title="Edit Group"
                              >
                                <FiEdit className="text-xs" />
                              </button>
                              <button 
                                type="button"
                                onClick={() => handleDeleteGroup(g.id)}
                                className="w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white flex items-center justify-center transition-all border border-rose-100 shadow-sm"
                                title="Delete Group"
                              >
                                <FiTrash2 className="text-xs" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Inline Group Input Row */}
                    {(showInputRow || !draft.groups || draft.groups.length === 0) && (
                      <tr className="border-b border-slate-100 bg-slate-50/30">
                        {/* GROUP NAME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-name align-middle">
                          <input 
                            type="text"
                            required
                            value={newGroup.pack_name}
                            onChange={(e) => setNewGroup({ ...newGroup, pack_name: e.target.value })}
                            placeholder="e.g. Morning Group"
                            className="w-full bg-[#fcfdfe] border border-red-400 focus:ring-4 focus:ring-rose-50 rounded-xl px-4 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 shadow-sm"
                          />
                        </td>
                        
                        {/* SELECT TEMPLATE */}
                        <td className="py-3 pr-2 assess-mod-groups-col-template align-middle">
                          <div className="relative w-full">
                            <select
                              value={newGroup.template_id || (draft.template?.id || '')}
                              onChange={(e) => {
                                const selectedTpl = templates.find(t => String(t.id) === String(e.target.value));
                                setNewGroup({
                                  ...newGroup,
                                  template_id: e.target.value,
                                  template_name: selectedTpl ? selectedTpl.name : ''
                                });
                              }}
                              className="w-full bg-[#fcfdfe] border border-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all appearance-none shadow-sm focus:ring-4 focus:ring-rose-50"
                            >
                              <option value="">Select Template</option>
                              {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <FiChevronDown className="text-slate-400 text-sm" />
                            </div>
                          </div>
                        </td>

                        {/* ASSIGNED ROLES */}
                        {!isHrFresher && (
                          <td className="py-3 pr-2 assess-mod-groups-col-roles align-middle">
                            <div className="assess-mod-groups-picker-wrap" ref={groupRolesDropdownRef}>
                              <GroupsInlineSearchField
                                value={roleSearchTerm}
                                onChange={(e) => {
                                  setRoleSearchTerm(e.target.value);
                                  setShowRolesDropdown(true);
                                }}
                                onFocus={() => setShowRolesDropdown(true)}
                                placeholder="Search roles..."
                                dropdownOpen={showRolesDropdown}
                                onToggleDropdown={() => {
                                  setShowRolesDropdown((open) => !open);
                                }}
                                selectionSummary={
                                  newGroup.user_role.length > 0 ? (
                                    <RoleSelectionSummary names={resolveRoleNames(newGroup.user_role)} />
                                  ) : null
                                }
                                toggleAriaLabel="Toggle roles list"
                              />
                              <GroupsPickerDropdownPortal
                                ref={groupRolesPanelRef}
                                isOpen={showRolesDropdown}
                                anchorRef={groupRolesDropdownRef}
                                minWidth={288}
                                title="Assigned Roles"
                                subtitle="Role List"
                                count={newGroup.user_role.length}
                              >
                                <div className="assess-mod-groups-member-panel__list">
                                  <GroupsPickerMemberRow
                                    isSelectAll
                                    selectAllLabel="Select All"
                                    checked={
                                      visibleGroupRoles.length > 0 &&
                                      visibleGroupRoles.every((r) =>
                                        newGroup.user_role.map(String).includes(String(r.id))
                                      )
                                    }
                                    onChange={(e) => selectAllGroupRoles(e.target.checked)}
                                  />
                                  {visibleGroupRoles.map((role) => (
                                    <GroupsPickerMemberRow
                                      key={role.id}
                                      name={role.name}
                                      checked={newGroup.user_role.map(String).includes(String(role.id))}
                                      onChange={() => toggleGroupRole(role.id)}
                                    />
                                  ))}
                                </div>
                                {visibleGroupRoles.length === 0 && (
                                  <p className="assess-mod-groups-member-panel__empty">No roles found</p>
                                )}
                              </GroupsPickerDropdownPortal>
                            </div>
                          </td>
                        )}

                        {/* ASSIGN TO */}
                        <td className="py-3 pr-2 assess-mod-groups-col-assign align-middle">
                          {isHrFresher ? (
                            <div className="relative w-full">
                              <select
                                value={newGroup.user_role[0] || ''}
                                onChange={(e) => {
                                  const selectedCollegeName = e.target.value;
                                  const templateObj = collegeTemplates.find(t => t.name === selectedCollegeName);
                                  setNewGroup({
                                    ...newGroup,
                                    user_role: selectedCollegeName ? [selectedCollegeName] : [],
                                    candidates: templateObj ? templateObj.student_ids : []
                                  });
                                }}
                                className="w-full bg-[#fcfdfe] border border-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-sm font-bold text-[#1e293b] outline-none transition-all appearance-none shadow-sm focus:ring-4 focus:ring-rose-50"
                              >
                                <option value="">Select College Name</option>
                                {collegeTemplates.map(t => (
                                  <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <FiChevronDown className="text-slate-400 text-sm" />
                              </div>
                            </div>
                          ) : (
                            <div className="assess-mod-groups-picker-wrap" ref={dropdownRef}>
                              <GroupsInlineSearchField
                                value={candidateSearchTerm}
                                onChange={(e) => {
                                  setCandidateSearchTerm(e.target.value);
                                  setShowCandidatesDropdown(true);
                                }}
                                onFocus={() => {
                                  if (!isHrFresher && newGroup.user_role.length === 0) return;
                                  setShowCandidatesDropdown(true);
                                }}
                                placeholder={
                                  isHrFresher
                                    ? 'Search candidates...'
                                    : (newGroup.user_role.length === 0 ? 'Select roles first' : 'Search candidates...')
                                }
                                disabled={!isHrFresher && newGroup.user_role.length === 0}
                                dropdownOpen={showCandidatesDropdown}
                                onToggleDropdown={() => {
                                  if (!isHrFresher && newGroup.user_role.length === 0) {
                                    toast.error('Please select roles first');
                                    return;
                                  }
                                  setShowCandidatesDropdown((open) => !open);
                                }}
                                selectionSummary={
                                  newGroup.candidates.length > 0 ? (
                                    <RoleSelectionSummary
                                      names={newGroup.candidates.map(id => {
                                        const emp = candidatesList.find(e => String(e.id) === String(id));
                                        return emp ? (emp.first_name || emp.name || String(id)) : String(id);
                                      })}
                                    />
                                  ) : null
                                }
                                toggleAriaLabel="Toggle candidates list"
                              />
                              <GroupsPickerDropdownPortal
                                ref={employeeDropdownPanelRef}
                                isOpen={showCandidatesDropdown}
                                anchorRef={dropdownRef}
                                minWidth={256}
                                title="Group Members"
                                subtitle="Candidate List"
                                count={newGroup.candidates.length}
                              >
                                <div className="assess-mod-groups-member-panel__list">
                                  <GroupsPickerMemberRow
                                    isSelectAll
                                    selectAllLabel="Select All Members"
                                    checked={(() => {
                                      const visible = getVisibleEmployees();
                                      return (
                                        visible.length > 0 &&
                                        visible.every((emp) =>
                                          newGroup.candidates.map(String).includes(String(emp.id))
                                        )
                                      );
                                    })()}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      const visible = getVisibleEmployees();
                                      const visibleIds = visible.map((emp) => String(emp.id));
                                      const current = Array.isArray(newGroup.candidates)
                                        ? newGroup.candidates.map(String)
                                        : [];
                                      const updated = checked
                                        ? [...new Set([...current, ...visibleIds])]
                                        : current.filter((id) => !visibleIds.includes(id));
                                      setNewGroup({ ...newGroup, candidates: updated });
                                    }}
                                  />
                                  {loadingCandidates ? (
                                    <div className="py-4 text-center">
                                      <div className="w-5 h-5 border border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-1" />
                                      <span className="text-[8px] font-black text-slate-400">Loading...</span>
                                    </div>
                                  ) : (
                                    getVisibleEmployees().map((emp) => (
                                      <GroupsPickerMemberRow
                                        key={emp.id}
                                        name={emp.first_name || emp.name || ''}
                                        meta={emp.email}
                                        checked={newGroup.candidates.map(String).includes(String(emp.id))}
                                        onChange={() => toggleEmployee(emp.id)}
                                      />
                                    ))
                                  )}
                                </div>
                                {!loadingCandidates && getVisibleEmployees().length === 0 && (
                                  <p className="assess-mod-groups-member-panel__empty">
                                    No candidates found
                                  </p>
                                )}
                              </GroupsPickerDropdownPortal>
                            </div>
                          )}
                        </td>

                        {/* DATE */}
                        <td className="py-3 pr-2 assess-mod-groups-col-date align-middle">
                          <div className="relative flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm hover:border-slate-300 transition-all w-full max-w-[150px] min-w-[130px] h-[36px]">
                            <span className="text-xs font-bold text-slate-700 tracking-wide select-none">
                              {formatDateToDDMMYYYY(newGroup.scheduled_date) || 'Select Date'}
                            </span>
                            <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <input 
                              type="date"
                              required
                              ref={dateInputRef}
                              className="assess-mod-groups-datetime-input absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              value={newGroup.scheduled_date}
                              onChange={(e) => setNewGroup({ ...newGroup, scheduled_date: e.target.value })}
                            />
                          </div>
                        </td>

                        {/* START TIME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-start align-middle">
                          <TimePicker12Hour
                            value={newGroup.start_time}
                            onChange={(val) => handleTimeFieldChange('start_time', val)}
                          />
                        </td>

                        {/* END TIME */}
                        <td className="py-3 pr-2 assess-mod-groups-col-end align-middle">
                          <TimePicker12Hour
                            value={newGroup.end_time}
                            onChange={(val) => handleTimeFieldChange('end_time', val)}
                          />
                        </td>

                        {/* DURATION */}
                        <td className="py-3 pr-2 assess-mod-groups-col-duration align-middle">
                          <div className="assess-mod-groups-duration-wrap">
                            <input 
                              type="number"
                              readOnly
                              value={newGroup.duration}
                              className="bg-transparent border-0 focus:ring-0 outline-none"
                            />
                            <span className="text-xs font-bold text-slate-400">M</span>
                          </div>
                        </td>

                        {/* ACTION */}
                        <td className="py-3 assess-mod-groups-col-action align-middle">
                          <div className="assess-mod-groups-action-wrap">
                            <button 
                              type="button"
                              onClick={handleAddGroupSubmit}
                              className="assess-mod-groups-action-btn bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white group"
                              title="Save Group"
                            >
                              <FiSave className="group-hover:scale-110 transition-transform" />
                            </button>
                            {(editingGroupId || (draft.groups && draft.groups.length > 0)) && (
                              <button 
                                type="button"
                                onClick={() => {
                                  setShowInputRow(false);
                                  setEditingGroupId(null);
                                  setNewGroup({
                                    pack_name: '',
                                    template_id: '',
                                    template_name: '',
                                    user_role: [],
                                    candidates: [],
                                    scheduled_date: new Date().toISOString().split('T')[0],
                                    start_time: '10:00',
                                    end_time: '11:00',
                                    duration: 60
                                  });
                                }}
                                className="assess-mod-groups-action-btn assess-mod-inventory-action-btn assess-mod-inventory-action-btn--red group"
                                title="Cancel"
                              >
                                <FiTrash2 className="group-hover:scale-110 transition-transform" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: REVIEW & PUBLISH */}
        {step === 'publish' && (
          <div className="space-y-6 animate-in fade-in duration-300 max-h-full overflow-y-auto custom-scrollbar pr-2 pb-4">
            
            {/* Custom Review & Publish Page Header */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm flex items-center justify-between shrink-0 mb-2">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.25-2.5 3.5-2.5 3.5s2.25-1 3.5-2.5M19.5 4.5c1.5-1.25 2.5-3.5 2.5-3.5s-2.25 1-3.5 2.5" />
                    <path d="M14 2c-3.5 0-7 3.5-7 7 0 1.5.5 3 1.5 4L11 15c1-1 2.5-1.5 4-1.5 3.5 0 7-3.5 7-7 0-2-1.5-3.5-3.5-3.5z" />
                    <path d="M9 15l-3 5H3l2-3 4-2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-extrabold text-slate-800 tracking-tight">Review & Publish</h1>
                </div>
              </div>
              
              {/* Top-Right Back Button */}
              <button 
                onClick={handleWizardBack}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
              >
                <FiArrowLeft className="text-slate-500" />
                <span>Back</span>
              </button>
            </div>

            {/* Three Summary Cards Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch shrink-0">
              
              {/* Card 1 – Test Information */}
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm flex flex-col">
                <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-50 pb-2">
                  <FiFileText className="text-red-500 text-sm" />
                  TEST INFORMATION
                </h3>
                <div className="flex-1 flex flex-col justify-between space-y-1">
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Test Name</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.name}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Category</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.category}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Type</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.type}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Instruction to the Exam Taker</span>
                    <span className="text-xs font-bold text-slate-800 text-left break-all flex-1">{draft.instructions}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 last:border-0">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Does it Include the Video</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.videoEnabled ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Card 2 – Question Bank */}
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm flex flex-col">
                <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-50 pb-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                  QUESTION BANK
                </h3>
                <div className="flex-1 flex flex-col justify-between space-y-1">
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Question Bank Name</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.questionBank?.name}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Total Questions</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.questionBank?.questions?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-2 last:border-0">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Question Type(s)</span>
                    <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">
                      {Array.from(new Set(draft.questionBank?.questions?.map(q => q.marks_type || q.type || 'MCQ'))).filter(Boolean).join(', ') || 'MCQ'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 3 – Structure & Scoring */}
              <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm flex flex-col">
                <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-50 pb-2">
                  <FiLayers className="text-red-500 text-sm" />
                  STRUCTURE & SCORING
                </h3>
                <div className="flex-1 flex flex-col space-y-1 justify-between">
                  <div>
                    <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Template Name</span>
                      <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.template?.name}</span>
                    </div>
                    <div className="flex justify-between items-start gap-4 py-2 border-b border-slate-100/60">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Pass Mark</span>
                      <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">{draft.template?.passMark} Marks</span>
                    </div>
                    <div className="flex justify-between items-start gap-4 py-2">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px] pt-0.5">Total Mark</span>
                      <span className="text-xs font-bold text-slate-800 text-right break-words flex-1">
                        {draft.template?.sections?.reduce((sum, sec) => {
                          const count = parseInt(sec.question_count || sec.count || 0, 10);
                          const marks = parseInt(sec.marks_each || sec.marks || 0, 10);
                          return sum + (count * marks);
                        }, 0) || 0} Marks
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                      Sections Configured
                    </span>
                    <div className="space-y-2 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                      {draft.template?.sections?.map((sec, idx) => (
                        <div key={idx} className="p-3 bg-slate-50/50 border border-slate-100 rounded-xl flex justify-between items-center text-xs font-bold text-slate-700">
                          <span>{sec.section_name || sec.name || `Section ${idx + 1}`}</span>
                          <span className="text-slate-500 font-semibold">
                            {sec.question_count || sec.count || 0} Qs · {sec.marks_each || sec.marks || 0}M each
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Assigned Cohorts & Schedule Section */}
            <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm mb-2 flex flex-col">
              <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                <FiUsers className="text-red-500 text-sm" />
                ASSIGNED COHORTS & SCHEDULE
              </h3>
              
              <div className="border border-slate-100 rounded-xl overflow-hidden shadow-inner">
                <div className="overflow-x-auto max-h-full">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/50 border-b border-slate-100">
                        <th className="py-3.5 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Group</th>
                        <th className="py-3.5 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Role</th>
                        <th className="py-3.5 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Candidates</th>
                        <th className="py-3.5 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Date & Time</th>
                        <th className="py-3.5 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.groups?.map(g => {
                        const rolesList = resolveRoleNames(g.user_role);
                        const candCount = String(g.candidates || '').split(',').filter(Boolean).length;
                        return (
                          <tr key={g.id} className="border-b border-slate-100/60 last:border-0 hover:bg-slate-50/20 transition-colors">
                            <td className="py-4 px-6 font-bold text-slate-700 text-sm">{g.pack_name}</td>
                            <td className="py-4 px-6">
                              <div className="flex flex-wrap gap-1.5 justify-start">
                                {rolesList.length > 0 ? (
                                  rolesList.map((roleName, rIdx) => (
                                    <span key={rIdx} className="bg-red-50 text-red-600 px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider">
                                      {roleName}
                                    </span>
                                  ))
                                ) : (
                                  <span className="bg-slate-50 text-slate-400 px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider">
                                    N/A
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-6 text-xs font-black text-slate-700 text-center">
                              {candCount} Candidates
                            </td>
                            <td className="py-4 px-6 text-xs font-semibold text-slate-500 text-center">
                              {formatDateToDDMMYYYY(g.scheduled_date)} at {formatTime12Hour(g.start_time)}
                            </td>
                            <td className="py-4 px-6 text-xs font-black text-slate-700 text-center">
                              {g.duration} min
                            </td>
                          </tr>
                        );
                      })}
                      {(!draft.groups || draft.groups.length === 0) && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">
                            No cohorts or groups assigned
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer Action Area */}
            <div className="shrink-0 bg-slate-50/90 backdrop-blur-sm py-4 px-6 rounded-2xl border border-slate-200/60 flex items-center justify-end mt-2">
              {/* Right Side */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleResetDraft}
                  className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-red-500 transition-colors bg-transparent border-0 outline-none cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 text-slate-400 hover:text-red-500 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                  <span>Reset Flow Draft</span>
                </button>
                
                <button
                  onClick={handlePublishAssessment}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-5 py-2.5 flex items-center gap-2 text-xs font-bold shadow-md hover:shadow-lg transition-all border-0 outline-none cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.25-2.5 3.5-2.5 3.5s2.25-1 3.5-2.5M19.5 4.5c1.5-1.25 2.5-3.5 2.5-3.5s-2.25 1-3.5 2.5" />
                    <path d="M14 2c-3.5 0-7 3.5-7 7 0 1.5.5 3 1.5 4L11 15c1-1 2.5-1.5 4-1.5 3.5 0 7-3.5 7-7 0-2-1.5-3.5-3.5-3.5z" />
                    <path d="M9 15l-3 5H3l2-3 4-2z" />
                  </svg>
                  <span>Save Test</span>
                </button>
              </div>
            </div>

          </div>
        )}

      </div>

      {/* Shared wizard navigation bottom bar (for steps 1-4) */}
      {step !== 'publish' && (
        <div className="absolute bottom-4 right-4 z-50 flex justify-end items-center gap-3 pointer-events-none">
          <button 
            onClick={handleSaveAndContinue}
            className="btn-assessment btn-assessment-danger flex items-center gap-2 text-xs font-bold pointer-events-auto shadow-lg"
          >
            <span>{(step === 'groups' && editingGroupId) ? 'Update & Continue' : (draft?.isEditMode ? 'Update & Continue' : 'Save & Continue')}</span>
            <FiArrowRight />
          </button>
          <button 
            onClick={handleSaveAndExit}
            className="btn-assessment btn-assessment-secondary flex items-center gap-2 text-xs font-bold pointer-events-auto shadow-lg"
          >
            <span>Save & Exit</span>
          </button>
        </div>
      )}



      {/* Double click viewing question details popup */}
      {viewingQuestion && (
        <AssessmentPopup
          isOpen={true}
          title="View Question Details"
          type="info"
          onCancel={() => setViewingQuestion(null)}
          confirmText="Close"
          onConfirm={() => setViewingQuestion(null)}
          message={
            <div className="text-left space-y-4">
              <div className="border-b pb-2">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Question</span>
                <p className="text-sm font-bold text-slate-800 mt-1 leading-relaxed">{viewingQuestion.question}</p>
              </div>
              {String(viewingQuestion.type).toUpperCase() === 'MCQ' && (
                <div className="grid grid-cols-2 gap-3">
                  {['a', 'b', 'c', 'd'].map(opt => {
                    const optText = viewingQuestion[`option_${opt}`] || viewingQuestion[`option_${opt.toUpperCase()}`];
                    const isCorrect = String(viewingQuestion.correct_answer).toUpperCase() === opt.toUpperCase();
                    return (
                      <div key={opt} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100'}`}>
                        <span className="text-[10px] font-black text-slate-400 uppercase">Option {opt.toUpperCase()}</span>
                        <p className="text-xs font-bold text-slate-700 mt-1 truncate">{optText || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Correct Answer</span>
                  <p className="text-xs font-bold text-emerald-600 mt-0.5">{viewingQuestion.correct_answer || '—'}</p>
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Marks</span>
                  <p className="text-xs font-bold text-slate-700 mt-0.5">{viewingQuestion.marks || '—'}</p>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Confirmation reset popup */}
      <AssessmentPopup
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </div>
  );
};

export const BatchWizard = ({ isOpen, onClose, assessment }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Group Flow</h3>
        <p className="text-gray-600 mb-6">Initializing group for: <strong>{assessment?.name}</strong></p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-assessment btn-assessment-secondary">Close</button>
          <button className="btn-assessment btn-assessment-danger">Initialize</button>
        </div>
      </div>
    </div>
  );
};

export default BatchWizard;
